import { useEffect, useRef, useState } from "react";
import { IconArrowsMinimize, IconMinus, IconPlus } from "@tabler/icons-react";
import { cn } from "../../lib/utils";
import Loader from "../Loader";
import { ViewerToolbarButton, ViewerToolbarDivider } from "./ViewerToolbar";

// Zoom against the fitted image: 1 shows it whole, 8 is as close as it goes.
const MIN_SCALE = 1;
const MAX_SCALE = 8;
// A click on − or +.
const BUTTON_FACTOR = 1.25;
// A wheel tick: one mouse notch (deltaY ≈ 100) is capped at a button step, a
// trackpad pinch (a few pixels per event) zooms smoothly.
const WHEEL_SENSITIVITY = 0.005;
const SNAP_TO_FIT = 1.01;
// A wheel that counts in lines (Firefox can) moves this many pixels a line.
const LINE_PX = 16;

type Transform = { scale: number; x: number; y: number };

const FITTED: Transform = { scale: MIN_SCALE, x: 0, y: 0 };

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

// A wheel distance in pixels, whatever unit the browser counted it in.
function wheelPixels(delta: number, event: WheelEvent, page: number): number {
  switch (event.deltaMode) {
    case WheelEvent.DOM_DELTA_PIXEL:
      return delta;
    case WheelEvent.DOM_DELTA_LINE:
      return delta * LINE_PX;
    case WheelEvent.DOM_DELTA_PAGE:
      return delta * page;
    default:
      throw new Error(
        `should not happen: deltaMode ${String(event.deltaMode)}`
      );
  }
}

/**
 * Keeps the zoomed image over the frame: it may not be moved further than its
 * overflow on each side, and a fitted image does not move at all.
 */
function bounded(
  next: Transform,
  frame: HTMLDivElement | null,
  image: HTMLImageElement | null
): Transform {
  if (frame === null || image === null) {
    throw new Error("should not happen: a zoom before the image is laid out");
  }
  const scale = clamp(next.scale, MIN_SCALE, MAX_SCALE);
  const maxX = Math.max(0, (image.offsetWidth * scale - frame.clientWidth) / 2);
  const maxY = Math.max(
    0,
    (image.offsetHeight * scale - frame.clientHeight) / 2
  );
  return {
    scale,
    x: clamp(next.x, -maxX, maxX),
    y: clamp(next.y, -maxY, maxY),
  };
}

/**
 * An image shown whole in its frame, which the reader zooms and moves around
 * (the full screen figure viewer). Ctrl + wheel, or a trackpad pinch, zooms on
 * the point under the cursor; − and + zoom on the centre. Zoomed, the image is
 * dragged by hand, or moved with the wheel.
 *
 * A UI primitive, so it owns its zoom: the transform is machinery. The caller
 * keys it on the image, so the next image opens fitted. It fills its parent.
 */
export function PanZoomImage({
  src,
  alt,
  zoomOutLabel,
  zoomInLabel,
  failure,
  fitLabel,
  toolbarStart,
  toolbarEnd,
  onLoad,
}: {
  src: string;
  alt: string;
  zoomOutLabel: string;
  zoomInLabel: string;
  /** What the frame says when the image does not load, and its retry. */
  failure: { message: string; retryLabel: string };
  /** The button that shows the image whole again, null where none is drawn. */
  fitLabel: string | null;
  /** The caller's own controls, before and after the zoom in the bar. */
  toolbarStart: React.ReactNode;
  toolbarEnd: React.ReactNode;
  /** The image painted; null where the caller does not need to know. */
  onLoad: (() => void) | null;
}): React.JSX.Element {
  const frameRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const [transform, setTransform] = useState<Transform>(FITTED);
  // The download of the image: a spinner until it paints, a message and a
  // retry when it fails. A retry loads the image again under a new key.
  const [load, setLoad] = useState<{
    attempt: number;
    status: "loading" | "ready" | "failed";
  }>({ attempt: 0, status: "loading" });
  // Where the pointer was at the last move: a drag adds each step to the
  // current transform, so a wheel zoom during the drag is kept. A ref, read
  // and written in the same event: several moves can arrive before React
  // renders again, and each must see where the previous one ended.
  const dragRef = useRef<{
    pointerId: number;
    lastX: number;
    lastY: number;
  } | null>(null);
  // The grabbing cursor only.
  const [isDragging, setIsDragging] = useState(false);

  function moved(current: Transform, dx: number, dy: number): Transform {
    return bounded(
      { ...current, x: current.x + dx, y: current.y + dy },
      frameRef.current,
      imageRef.current
    );
  }

  // Zooms by `factor` and keeps the image point under (dx, dy), measured from
  // the frame's centre, where it was.
  function zoomed(
    from: Transform,
    factor: number,
    dx: number,
    dy: number
  ): Transform {
    const target = clamp(from.scale * factor, MIN_SCALE, MAX_SCALE);
    // A pinch back out rarely lands on 1 exactly: close enough is fitted, so
    // − turns off and the wheel is let go. Only on the way out: a slow pinch
    // in starts that close to 1.
    const scale =
      factor < 1 && target < MIN_SCALE * SNAP_TO_FIT ? MIN_SCALE : target;
    const ratio = scale / from.scale;
    return bounded(
      {
        scale,
        x: dx - (dx - from.x) * ratio,
        y: dy - (dy - from.y) * ratio,
      },
      frameRef.current,
      imageRef.current
    );
  }

  // The wheel goes through a native listener: React's is passive, and Ctrl +
  // wheel must not zoom the whole page as well.
  useEffect(() => {
    const frame = frameRef.current;
    if (frame === null) {
      throw new Error("should not happen: no frame after mount");
    }
    const handleWheel = (event: WheelEvent): void => {
      // A pinch on a trackpad reaches the page as Ctrl + wheel.
      if (event.ctrlKey || event.metaKey) {
        event.preventDefault();
        const rect = frame.getBoundingClientRect();
        const factor = clamp(
          Math.exp(
            -wheelPixels(event.deltaY, event, frame.clientHeight) *
              WHEEL_SENSITIVITY
          ),
          1 / BUTTON_FACTOR,
          BUTTON_FACTOR
        );
        setTransform((current) =>
          zoomed(
            current,
            factor,
            event.clientX - rect.left - rect.width / 2,
            event.clientY - rect.top - rect.height / 2
          )
        );
        return;
      }
      // Fitted, there is nothing to move: the wheel is left alone.
      if (transform.scale === MIN_SCALE) return;
      event.preventDefault();
      const dx = -wheelPixels(event.deltaX, event, frame.clientWidth);
      const dy = -wheelPixels(event.deltaY, event, frame.clientHeight);
      setTransform((current) => moved(current, dx, dy));
    };
    frame.addEventListener("wheel", handleWheel, { passive: false });
    return () => {
      frame.removeEventListener("wheel", handleWheel);
    };
  });

  // A resized window resizes the fitted image too: the offsets are bounded
  // again, so no blank strip is left at an edge.
  useEffect(() => {
    const frame = frameRef.current;
    if (frame === null) {
      throw new Error("should not happen: no frame after mount");
    }
    const observer = new ResizeObserver(() => {
      setTransform((current) => {
        const next = bounded(current, frame, imageRef.current);
        return next.x === current.x && next.y === current.y ? current : next;
      });
    });
    observer.observe(frame);
    return () => {
      observer.disconnect();
    };
  }, []);

  const isZoomed = transform.scale > MIN_SCALE;

  return (
    <div
      ref={frameRef}
      className={cn(
        "relative size-full touch-none overflow-hidden select-none",
        isDragging
          ? "cursor-grabbing"
          : isZoomed
            ? "cursor-grab"
            : "cursor-default"
      )}
      onPointerDown={(event) => {
        if (!isZoomed || event.button !== 0) return;
        event.currentTarget.setPointerCapture(event.pointerId);
        dragRef.current = {
          pointerId: event.pointerId,
          lastX: event.clientX,
          lastY: event.clientY,
        };
        setIsDragging(true);
      }}
      onPointerMove={(event) => {
        const drag = dragRef.current;
        if (drag === null || drag.pointerId !== event.pointerId) return;
        const dx = event.clientX - drag.lastX;
        const dy = event.clientY - drag.lastY;
        dragRef.current = {
          pointerId: drag.pointerId,
          lastX: event.clientX,
          lastY: event.clientY,
        };
        setTransform((current) => moved(current, dx, dy));
      }}
      onPointerUp={() => {
        dragRef.current = null;
        setIsDragging(false);
      }}
      onPointerCancel={() => {
        dragRef.current = null;
        setIsDragging(false);
      }}
    >
      {/* The fitted image keeps a margin; zoomed, it uses the whole frame. */}
      <div className="flex size-full items-center justify-center p-16">
        <img
          key={load.attempt}
          ref={imageRef}
          src={src}
          alt={alt}
          draggable={false}
          onLoad={() => {
            setLoad((current) => ({ ...current, status: "ready" }));
            if (onLoad !== null) onLoad();
          }}
          onError={() => {
            setLoad((current) => ({ ...current, status: "failed" }));
          }}
          className={cn(
            "max-h-full max-w-full object-contain",
            load.status === "ready" ? null : "invisible"
          )}
          style={{
            transform: `translate(${String(transform.x)}px, ${String(transform.y)}px) scale(${String(transform.scale)})`,
          }}
        />
      </div>

      {load.status === "loading" ? (
        <div className="absolute inset-0 flex items-center justify-center">
          <Loader expanded />
        </div>
      ) : null}
      {load.status === "failed" ? (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 p-6 text-center">
          <p className="m-0 text-ui-body text-ds-slate-700">
            {failure.message}
          </p>
          <button
            type="button"
            onClick={() => {
              setLoad((current) => ({
                attempt: current.attempt + 1,
                status: "loading",
              }));
            }}
            className="h-9 cursor-pointer rounded-md border border-ds-slate-100 bg-ds-white px-4 text-footnote font-medium text-ds-mint-900 hover:bg-ds-slate-50"
          >
            {failure.retryLabel}
          </button>
        </div>
      ) : null}

      <div
        className={cn(
          "absolute bottom-4 left-1/2 flex -translate-x-1/2 items-center gap-1 rounded-lg border border-ds-slate-100 bg-ds-white p-1 shadow-popover",
          load.status === "ready" ? null : "hidden"
        )}
        // A press on the controls is a click, not the start of a drag.
        onPointerDown={(event) => {
          event.stopPropagation();
        }}
      >
        {toolbarStart === null ? null : (
          <>
            {toolbarStart}
            <ViewerToolbarDivider />
          </>
        )}
        <ViewerToolbarButton
          label={zoomOutLabel}
          isDisabled={!isZoomed}
          onClick={() => {
            setTransform(zoomed(transform, 1 / BUTTON_FACTOR, 0, 0));
          }}
        >
          <IconMinus stroke={1.5} />
        </ViewerToolbarButton>
        <span className="min-w-14 text-center text-footnote font-semibold text-ds-black">
          {`${String(Math.round(transform.scale * 100))} %`}
        </span>
        <ViewerToolbarButton
          label={zoomInLabel}
          isDisabled={transform.scale === MAX_SCALE}
          onClick={() => {
            setTransform(zoomed(transform, BUTTON_FACTOR, 0, 0));
          }}
        >
          <IconPlus stroke={1.5} />
        </ViewerToolbarButton>
        {fitLabel === null && toolbarEnd === null ? null : (
          <ViewerToolbarDivider />
        )}
        {fitLabel === null ? null : (
          <ViewerToolbarButton
            label={fitLabel}
            isDisabled={!isZoomed}
            onClick={() => {
              setTransform(FITTED);
            }}
          >
            <IconArrowsMinimize stroke={1.5} />
          </ViewerToolbarButton>
        )}
        {toolbarEnd}
      </div>
    </div>
  );
}
